// THE SOUND MAP — which sound plays for which game event. The shipped assignments are SfxEvents[].def; a player's own assignments
// (made on /testing-field.html, the Sounds tab) live in THIS browser only: localStorage holds the map, IndexedDB holds any sound
// files they uploaded. The game plays through playEvent(id, …args), so an assignment made on the Testing Field is heard in play at
// once (after a reload). "Export" on that page copies the map as text for the repo. Pure parts (resolve, defaultMap) are tested in
// scripts/qa/sfxmap.test.mjs.

import * as sfx from './sfx.js'

// Every sound the game can make: the built-in synths (sfx.js) — "name" or "name:arg" — plus 'none' and any 'file:<name>' uploaded.
export const BUILTIN_SOUNDS = Object.freeze([
  'click', 'rowFilled', 'surplus', 'comboMax', 'comboBreak',
  'seasonTurn:PREP', 'seasonTurn:PLANT', 'seasonTurn:MAINTAIN', 'seasonTurn:HARVEST',
  'harvest', 'crowCaw', 'crowEat', 'shoo', 'glint', 'glint:caught', 'coinPop', 'kneel',
])
// The events, in the order the board shows them. `arg` says what the game passes (the click's combo pitch).
export const SfxEvents = Object.freeze([
  { id: 'click', label: 'Paw click', when: 'every press of the paw — the pitch climbs with the combo', def: 'click', arg: 'combo' },
  { id: 'rowFilled', label: 'Row filled', when: 'a crop row fills (every 5 clicks)', def: 'rowFilled' },
  { id: 'surplus', label: 'Surplus click', when: 'a click past a full field', def: 'surplus' },
  { id: 'comboMax', label: 'Combo at the top', when: 'the rhythm meter fills', def: 'comboMax' },
  { id: 'comboBreak', label: 'Combo breaks', when: 'a pause longer than a second', def: 'comboBreak' },
  { id: 'seasonPREP', label: 'Season turns · PREP', when: 'a new year begins after the harvest', def: 'seasonTurn:PREP' },
  { id: 'seasonPLANT', label: 'Season turns · PLANT', when: 'the paddies flood', def: 'seasonTurn:PLANT' },
  { id: 'seasonMAINTAIN', label: 'Season turns · MAINTAIN', when: 'the rice stands tall', def: 'seasonTurn:MAINTAIN' },
  { id: 'seasonHARVEST', label: 'Season turns · HARVEST', when: 'the rice turns gold', def: 'seasonTurn:HARVEST' },
  { id: 'harvest', label: 'Harvest payout', when: 'the end of HARVEST pays out', def: 'harvest' },
  { id: 'crowLand', label: 'Crow lands', when: 'a crow lands on a row', def: 'crowCaw' },
  { id: 'crowShoo', label: 'Crow shooed', when: 'you click the crow', def: 'shoo' },
  { id: 'crowAte', label: 'Crow eats a notch', when: 'the crow was left for 6 seconds', def: 'crowEat' },
  { id: 'glintUp', label: 'Glint appears', when: 'a golden glint sparkles on a row', def: 'glint' },
  { id: 'glintCaught', label: 'Glint caught', when: 'a paw click while the glint is up', def: 'glint:caught' },
  { id: 'coin', label: 'Citizen coin', when: 'a directed citizen pulls a plant and drops a coin', def: 'coinPop' },
  { id: 'kneel', label: 'Citizen kneels', when: 'a citizen kneels to tend (unassigned by default)', def: 'none' },
  { id: 'message', label: 'Message arrives', when: 'a card rises onto the message rail (unassigned by default)', def: 'none' },
  { id: 'emperor', label: 'The Emperor', when: 'his warning appears (unassigned by default)', def: 'none' },
])

export const defaultMap = () => Object.fromEntries(SfxEvents.map((e) => [e.id, e.def]))
// The sound for an event: the override when it names something valid, else the default; unknown events are 'none'.
export function resolve(map, overrides, id) {
  const o = overrides?.[id]
  if (typeof o === 'string' && (o === 'none' || BUILTIN_SOUNDS.includes(o) || o.startsWith('file:'))) return o
  return map?.[id] ?? 'none'
}
export const isFile = (sound) => typeof sound === 'string' && sound.startsWith('file:')
export const fileName = (sound) => (isFile(sound) ? sound.slice(5) : null)

// ── this browser's assignments ─────────────────────────────────────────────────────────────────────────────────
const MAP_KEY = 'mk-sfx-map'
const store = () => { try { return window.localStorage } catch { return null } }
export function loadOverrides() { try { const o = JSON.parse(store()?.getItem(MAP_KEY) || '{}'); return o && typeof o === 'object' ? o : {} } catch { return {} } }
export function saveOverrides(o) { try { store()?.setItem(MAP_KEY, JSON.stringify(o)) } catch { /* private mode */ } }
export function setOverride(id, sound) { const o = loadOverrides(); if (sound == null || sound === resolve(defaultMap(), {}, id)) delete o[id]; else o[id] = sound; saveOverrides(o); return o }
export function clearOverrides() { saveOverrides({}) }
export const exportText = () => JSON.stringify({ map: { ...defaultMap(), ...loadOverrides() }, files: Object.values(loadOverrides()).filter(isFile).map(fileName) }, null, 2)

// ── uploaded sound files (IndexedDB) ───────────────────────────────────────────────────────────────────────────
const DB = 'mk-sfx', STORE = 'files'
function db() {
  return new Promise((res, rej) => {
    if (typeof indexedDB === 'undefined') return rej(new Error('no IndexedDB'))
    const r = indexedDB.open(DB, 1)
    r.onupgradeneeded = () => r.result.createObjectStore(STORE)
    r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error)
  })
}
const tx = (mode, fn) => db().then((d) => new Promise((res, rej) => { const t = d.transaction(STORE, mode); const req = fn(t.objectStore(STORE)); t.oncomplete = () => res(req?.result); t.onerror = () => rej(t.error) }))
export const putFile = (name, blob) => tx('readwrite', (s) => s.put(blob, name))
export const deleteFile = (name) => tx('readwrite', (s) => s.delete(name))
export const getFile = (name) => tx('readonly', (s) => s.get(name))
export const listFiles = () => tx('readonly', (s) => s.getAllKeys()).catch(() => [])

// ── play ───────────────────────────────────────────────────────────────────────────────────────────────────────
let overrides = null
const current = () => (overrides ??= loadOverrides())
export function refreshOverrides() { overrides = loadOverrides() }
// The game calls this: the event's assigned sound, with the event's argument (the click's combo).
export function playEvent(id, ...args) {
  const sound = resolve(defaultMap(), current(), id)
  if (sound === 'none') return sound
  if (isFile(sound)) { sfx.playFile(fileName(sound), getFile); return sound }
  sfx.playById(sound, ...args)
  return sound
}
if (typeof window !== 'undefined' && import.meta.env?.DEV) window.__sfxMap = { events: SfxEvents, builtins: BUILTIN_SOUNDS, resolve: (id) => resolve(defaultMap(), current(), id), set: (id, s) => { setOverride(id, s); refreshOverrides() }, clear: () => { clearOverrides(); refreshOverrides() }, overrides: () => ({ ...current() }), play: playEvent }
